import { Observable, Subject } from 'rxjs';

import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { omit } from 'lodash';
import { map } from 'rxjs/operators';
import { ModelAdmin } from '../models/model-admin.model';
import { AuthService } from './auth.service';
import { toHttpParams } from './http-query-params';

@Injectable({ providedIn: 'root' })
export class ModelAdminService {
  private _http = inject(HttpClient);
  protected authService = inject(AuthService);

  update = new Subject<{ operation: string; value: ModelAdmin[] }>();

  getModelAdminList(query: ModelAdminQuery = {}): Observable<{ statusCode: number; data: Data }> {
    const route: Record<keyof ModelAdminQuery, string> = { ids: 'ids', offset: 'offset', limit: 'limit' };

    return this._http
      .get<{ data: Data; statusCode: number }>('/api/model-admin/list', { observe: 'response', params: toHttpParams(query, route) })
      .pipe(
        map(res => {
          return res.body!;
        }),
      );
  }

  getModelAdmin(modelAdminId: number): Observable<{ statusCode: number; data: ModelAdmin }> {
    return this._http.get<{ data: ModelAdmin; statusCode: number }>('/api/model-admin/' + modelAdminId, { observe: 'response' }).pipe(
      map(res => {
        return res.body!;
      }),
    );
  }

  addModelAdmin(newModelAdmin: ModelAdmin): Observable<EmptyMutationResponse> {
    return this._http.post<void>('/api/model-admin', newModelAdmin, { observe: 'response' }).pipe(
      map(res => {
        this.update.next({ operation: 'addModelAdmin', value: [] });

        return toEmptyMutationResponse(res);
      }),
    );
  }

  updateModelAdmin(modelAdminData: ModelAdmin): Observable<EmptyMutationResponse> {
    const modelAdmin = this.authService.isSuperAdmin() ? modelAdminData : omit(modelAdminData, ['fullName']);

    return this._http.put<void>('/api/model-admin', modelAdmin, { observe: 'response' }).pipe(
      map(res => {
        this.update.next({ operation: 'updateModelAdmin', value: [] });

        return toEmptyMutationResponse(res);
      }),
    );
  }

  deleteModelAdmin(modelAdminId: number): Observable<EmptyMutationResponse> {
    return this._http.delete<void>('/api/model-admin/' + modelAdminId, { observe: 'response' }).pipe(
      map(res => {
        this.update.next({ operation: 'deleteModelAdmin', value: [] });

        return toEmptyMutationResponse(res);
      }),
    );
  }

  setPassword(passwords: { currentPassword: string; newPassword: string }): Observable<EmptyMutationResponse> {
    return this._http.put<void>('/api/model-admin/set-password', passwords, { observe: 'response' }).pipe(map(toEmptyMutationResponse));
  }
}

type EmptyMutationResponse = { statusCode: number };

const toEmptyMutationResponse = (response: HttpResponse<unknown>): EmptyMutationResponse => ({ statusCode: response.status });

export type Data = {
  listing: ModelAdmin[];
  total: number;
};

export type ModelAdminQuery = {
  ids?: number[];
  limit?: number;
  offset?: number;
};
